import type { FileRecord } from "@/shared/domain/files";

/**
 * The metadata key on the **source** file under which the platform records the
 * state of its generated visualization asset.
 *
 * The generated glTF lives under `.VISUALIZATION_ASSETS/`, a leading-dot path
 * that directory browse and search both filter out — so this block is the only
 * view of the artifact that is not hidden, and it is what the wizard polls.
 *
 * Writing it bumps the source record's version, because file records are
 * append-only. That is harmless: nothing is keyed on `version` any more — the
 * artifact's path is derived from `upload_id` (see `visualizationAssetPaths`),
 * which no write to the record can move.
 */
export const VISUALIZATION_ASSET_METADATA_KEY = "roboto/visualization_asset";

/** How often the wizard re-reads the source record while conversion runs. */
export const VISUALIZATION_ASSET_POLL_INTERVAL_MS = 2000;

/**
 * Where a conversion has got to.
 *
 * The worker writes `processing` as its first act and a terminal status as its
 * last, so a wizard can tell queued (no block at all) from running from done
 * without a job table.
 */
export type VisualizationAssetStatus = "processing" | "completed" | "failed";

/** Whether this status is one the worker will not move off. */
export function isTerminalVisualizationAssetStatus(
  status: VisualizationAssetStatus | undefined,
): boolean {
  return status === "completed" || status === "failed";
}

/**
 * The knobs the URDF converter exposes, as the request body spells them.
 *
 * `output_format` is forced to `separate` and the destination path is derived
 * by the platform, so neither is settable here. Snake case because these cross
 * the wire verbatim.
 */
export interface VisualizationAssetSettings {
  /** Use each link's `<collision>` geometry rather than its `<visual>`. */
  collision: boolean;
  /** Keep the source's Z-up frame instead of converting to glTF's Y-up. */
  z_up: boolean;
  /** Fail the conversion on an unresolvable reference instead of dropping it. */
  strict: boolean;
  /** Emit the `ROBOTO_device` kinematic tree, without which nothing articulates. */
  articulation: boolean;
  /** Extra `name=path` package roots for resolving `package://` URIs. */
  packages: string;
}

export const DEFAULT_VISUALIZATION_ASSET_SETTINGS: VisualizationAssetSettings =
  {
    collision: false,
    z_up: false,
    strict: false,
    articulation: true,
    packages: "",
  };

/**
 * The status block the worker writes onto the source file's metadata.
 *
 * Mirrors the backend contract exactly. Everything but `status` is optional
 * here even where the backend always writes it, because this is parsed out of a
 * free-form metadata dict: a block from an older schema version, or one written
 * half-way through a deploy, must degrade to "we know the status" rather than
 * to "we cannot read this at all".
 */
export interface VisualizationAssetRecord {
  schema_version?: number;
  status: VisualizationAssetStatus;
  /** Dataset-relative path of the generated `.gltf`. */
  relative_path?: string;
  /**
   * The upload transaction of the source file this artifact was built from.
   *
   * The staleness signal: it changes if and only if the URDF's bytes are
   * re-uploaded, so a block naming a different upload than the record's current
   * one describes an artifact built from content this file no longer holds. See
   * `ingestPhase`, which treats such a block as absent.
   */
  source_upload_id?: string;
  settings?: Partial<VisualizationAssetSettings>;
  /**
   * References the converter could not resolve and therefore dropped.
   *
   * Routinely non-empty: an unresolvable `package://` mesh is the normal case,
   * not an exception, and the model will be missing that geometry. The wizard
   * has to say so rather than present a silently incomplete robot.
   */
  unresolved?: string[];
  joint_count?: number;
  error?: string | null;
  generated?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStatus(value: unknown): VisualizationAssetStatus | undefined {
  return value === "processing" || value === "completed" || value === "failed"
    ? value
    : undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * The visualization-asset block on a file record, or `null` if it has none.
 *
 * Returns `null` — rather than throwing — for a block that is absent,
 * malformed, or carries an unrecognised status. A file with no block has simply
 * never been ingested, which is the overwhelmingly common case and not an
 * error; and a status this build does not know about is indistinguishable from
 * that as far as the wizard can act on it.
 *
 * The value may arrive as a JSON string: file metadata is typed
 * `Record<string, string>` on the client but is a free-form JSON dict on the
 * backend, and which of the two a given deployment returns is not something a
 * reader should have to care about.
 */
export function readVisualizationAsset(
  record: Pick<FileRecord, "metadata"> | undefined,
): VisualizationAssetRecord | null {
  const raw: unknown = record?.metadata?.[VISUALIZATION_ASSET_METADATA_KEY];
  const parsed = typeof raw === "string" ? tryParseJson(raw) : raw;
  if (!isRecord(parsed)) {
    return null;
  }

  const status = asStatus(parsed.status);
  if (status === undefined) {
    return null;
  }

  const settings = isRecord(parsed.settings)
    ? (parsed.settings as Partial<VisualizationAssetSettings>)
    : undefined;

  return {
    status,
    ...(asNumber(parsed.schema_version) !== undefined
      ? { schema_version: asNumber(parsed.schema_version) }
      : {}),
    ...(asString(parsed.relative_path) !== undefined
      ? { relative_path: asString(parsed.relative_path) }
      : {}),
    ...(asString(parsed.source_upload_id) !== undefined
      ? { source_upload_id: asString(parsed.source_upload_id) }
      : {}),
    ...(settings ? { settings } : {}),
    ...(asStringArray(parsed.unresolved) !== undefined
      ? { unresolved: asStringArray(parsed.unresolved) }
      : {}),
    ...(asNumber(parsed.joint_count) !== undefined
      ? { joint_count: asNumber(parsed.joint_count) }
      : {}),
    ...(asString(parsed.error) !== undefined
      ? { error: asString(parsed.error) }
      : {}),
    ...(asString(parsed.generated) !== undefined
      ? { generated: asString(parsed.generated) }
      : {}),
  };
}

function tryParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}
